/**
 * Module 'adapters': the agent adapter boundary (product architecture 6.4, cost doc 11,
 * docs/architecture/adapters.md). A session is agent-agnostic: `agentKind` names the
 * adapter that reported it, `state` is the adapter's mapping onto the canonical state
 * machine, and `ext` is a blob only that adapter writes. What each provider can and
 * cannot do is data here, so the UI can show a missing capability as unavailable
 * instead of hiding it.
 */
import type { Session, SessionStatus, Ts } from '../index.ts';
import type { CanonicalSessionState } from './missions.ts';
import { sessionFailed } from './missions.ts';
import { ACP_CONFIG_CATEGORIES, ACP_CONFIG_MAX_VALUES, ACP_CONFIG_NAME_MAX, ACP_CONFIG_VALUE_MAX, acpAgent, advertisedValues } from './agent-kinds.ts';
import type { AcpAdvertisedConfig, AcpAgentDefinition, AcpConfigValue, AcpRuntimeSummary } from './agent-kinds.ts';

// ---------------------------------------------------------------------------
// Kinds
// ---------------------------------------------------------------------------

export type KnownAgentKind = 'claude-code' | 'codex';
/** An adapter's kind. Known kinds get labels and icons; any other string is shown as is. */
export type AgentKind = KnownAgentKind | (string & {});

export const KNOWN_AGENT_KINDS: readonly KnownAgentKind[] = ['claude-code', 'codex'];
/** Sessions from agents that predate adapters (and the Go agent) carry no kind: they are Claude Code. */
export const DEFAULT_AGENT_KIND: KnownAgentKind = 'claude-code';

export const AGENT_KIND_LABEL: Record<KnownAgentKind, string> = {
  'claude-code': 'Claude Code',
  codex: 'Codex',
};

export function isKnownAgentKind(v: unknown): v is KnownAgentKind {
  return typeof v === 'string' && (KNOWN_AGENT_KINDS as readonly string[]).includes(v);
}

/** The kind of a session, defaulting to Claude Code for reports without one. */
export function agentKindOf(s: Pick<Session, 'agentKind'> | null | undefined): AgentKind {
  return s?.agentKind || DEFAULT_AGENT_KIND;
}

export function agentKindLabel(kind: AgentKind | null | undefined): string {
  const k = kind || DEFAULT_AGENT_KIND;
  return isKnownAgentKind(k) ? AGENT_KIND_LABEL[k] : (acpAgent(k)?.label ?? k);
}

/** A kind is a plain token: lowercase letters, digits and dashes. */
export function isAgentKindToken(v: unknown): v is AgentKind {
  return typeof v === 'string' && /^[a-z][a-z0-9-]{1,39}$/.test(v);
}

// ---------------------------------------------------------------------------
// Capabilities
// ---------------------------------------------------------------------------

/** What Orgus can do with a provider's sessions, one entry per capability the UI shows. */
export type AgentCapability =
  | 'discover'
  | 'launch.headless'
  | 'launch.terminal'
  | 'stop'
  | 'resume'
  | 'waiting'
  | 'approvals'
  | 'questions'
  | 'subagents'
  | 'workflows'
  | 'tokens'
  | 'permissions';

export const AGENT_CAPABILITY_ORDER: readonly AgentCapability[] = [
  'discover',
  'launch.headless',
  'launch.terminal',
  'stop',
  'resume',
  'waiting',
  'approvals',
  'questions',
  'subagents',
  'workflows',
  'tokens',
  'permissions',
];

export const AGENT_CAPABILITY_LABEL: Record<AgentCapability, string> = {
  discover: 'See sessions started elsewhere',
  'launch.headless': 'Start headless',
  'launch.terminal': 'Start in a terminal',
  stop: 'Stop',
  resume: 'Resume command',
  waiting: 'Knows when it waits for you',
  approvals: 'Answer approvals from Orgus',
  questions: 'Answer its questions and plans from Orgus',
  subagents: 'Subagents',
  workflows: 'Workflows',
  tokens: 'Token usage',
  permissions: 'Permission modes',
};

export type AgentSupport = 'yes' | 'partial' | 'no';

export interface AgentCapabilityInfo {
  support: AgentSupport;
  /** What partial or missing support means, in a sentence a person can act on. */
  note?: string;
}

export type AgentCapabilities = Record<AgentCapability, AgentCapabilityInfo>;

const YES: AgentCapabilityInfo = { support: 'yes' };

/** The honest matrix: what this version of Orgus does with each provider. */
export const AGENT_CAPABILITIES: Record<KnownAgentKind, AgentCapabilities> = {
  'claude-code': {
    discover: YES,
    'launch.headless': YES,
    'launch.terminal': YES,
    stop: YES,
    resume: YES,
    waiting: { support: 'yes', note: 'From the transcript and the process record: a question, a plan to approve, or a tool call held for permission.' },
    approvals: { support: 'partial', note: 'Only for sessions Orgus starts, through per-session hooks. Watched sessions are answered in their own terminal.' },
    questions: { support: 'yes', note: 'AskUserQuestion, ExitPlanMode and MCP elicitations become asks in the Inbox; the delegate or a person answers them.' },
    subagents: YES,
    workflows: YES,
    tokens: YES,
    permissions: YES,
  },
  codex: {
    discover: { support: 'yes', note: 'From the rollout files in ~/.codex/sessions (or $CODEX_HOME). Rollouts older than 7 days are compressed and skipped.' },
    'launch.headless': { support: 'yes', note: 'Runs codex exec --json with the prompt on stdin.' },
    'launch.terminal': {
      support: 'yes',
      note: 'Opens codex, or codex resume <id>, in a new terminal window on that machine. A one-word prompt, or one through codex.cmd on Windows, is shown there to paste.',
    },
    stop: {
      support: 'partial',
      note: 'Only headless sessions Orgus started. Terminal launches cannot be stopped today. Codex keeps no process records, so a watched session is stopped in its own terminal.',
    },
    resume: { support: 'yes', note: 'codex resume <id> in the session folder, or codex exec resume <id> headless when Orgus wakes an ended session.' },
    waiting: {
      support: 'partial',
      note: 'Codex does not write approval prompts to its rollout, so only a finished turn that ends in a question shows as waiting.',
    },
    approvals: { support: 'no', note: 'Codex has no remote approval hook. Answer approval prompts in the Codex terminal.' },
    questions: {
      support: 'partial',
      note: 'A headless run that ends with a question becomes an ask; the answer resumes the same session with codex exec resume. Codex cannot ask mid-turn.',
    },
    subagents: { support: 'no', note: 'Codex sub-agents are separate rollouts and appear as their own sessions.' },
    workflows: { support: 'no', note: 'Workflows are a Claude Code feature.' },
    tokens: { support: 'yes', note: 'Input, cached input and output tokens from Codex token counts. Reasoning tokens are part of output.' },
    permissions: {
      support: 'partial',
      note: 'Mapped onto Codex sandbox modes: Codex default keeps the setting Codex has (read-only for a headless run, which never asks), Plan only is read-only, Accept edits is workspace-write, Bypass is full access.',
    },
  },
};

const UNKNOWN_KIND_NOTE = 'This agent kind is not known to this version of Orgus.';

/**
 * What the generic ACP adapter does for any ACP agent (ADR 0049 decision 2), before the
 * definition's verified notes and what the agent's `initialize` reported.
 */
export const ACP_BASE_CAPABILITIES: AgentCapabilities = {
  discover: { support: 'no', note: "ACP shows only sessions Orgus starts. Sessions started in the agent's own terminal stay invisible." },
  'launch.headless': { support: 'yes', note: 'Runs the agent in ACP mode, hidden, with the prompt sent over its standard input.' },
  'launch.terminal': { support: 'no', note: 'Not built for ACP agents yet. Start it headless, or run it in a terminal on that machine.' },
  stop: { support: 'yes', note: 'Cancels the turn, closes the session, then ends the process Orgus started.' },
  resume: { support: 'partial', note: "When the agent can load an earlier session. Orgus knows after the agent's first start on that machine." },
  waiting: { support: 'yes', note: 'A permission request waiting for an answer, or a finished turn.' },
  approvals: { support: 'yes', note: 'Every file edit and command reaches Orgus as a permission request, answered once at a time, never always.' },
  questions: { support: 'yes', note: 'A plan that needs approval and an elicitation become asks in the Inbox; the delegate or a person answers them.' },
  subagents: { support: 'no', note: 'Subagents run inside the agent and ACP does not report them.' },
  workflows: { support: 'no', note: 'Workflows are a Claude Code feature.' },
  tokens: { support: 'partial', note: 'Context use and cost when the agent reports them. ACP has no per-type token counts yet.' },
  permissions: { support: 'partial', note: "Mapped onto the agent's own modes where it offers them. Orgus never picks a mode that skips approvals." },
};

/**
 * The capability row of an ACP agent: the generic row, the definition's verified notes, then
 * what `initialize` reported on a machine (resume only when the agent can load or resume).
 */
export function acpCapabilities(def: AcpAgentDefinition, summary?: AcpRuntimeSummary | null): AgentCapabilities {
  const out: AgentCapabilities = { ...ACP_BASE_CAPABILITIES };
  for (const c of AGENT_CAPABILITY_ORDER) {
    const own = def.capabilities[c];
    if (own) out[c] = own;
  }
  if (summary) {
    const canResume = summary.loadSession || summary.sessions.resume;
    out.resume = canResume
      ? { support: 'yes', note: summary.loadSession ? 'Loads the earlier session with its history.' : 'Resumes the earlier session without replaying its history.' }
      : { support: 'no', note: `This version of ${def.label} cannot load an earlier session over ACP. Start a new session instead.` };
  }
  return out;
}

/** The capabilities of a kind; an unknown kind supports nothing but discovery. */
export function agentCapabilities(kind: AgentKind | null | undefined): AgentCapabilities {
  const k = kind || DEFAULT_AGENT_KIND;
  if (isKnownAgentKind(k)) return AGENT_CAPABILITIES[k];
  const acp = acpAgent(k);
  if (acp) return acpCapabilities(acp);
  const out = {} as AgentCapabilities;
  for (const c of AGENT_CAPABILITY_ORDER) out[c] = c === 'discover' ? YES : { support: 'no', note: UNKNOWN_KIND_NOTE };
  return out;
}

export function agentSupports(kind: AgentKind | null | undefined, cap: AgentCapability): boolean {
  return agentCapabilities(kind)[cap].support !== 'no';
}

// ---------------------------------------------------------------------------
// Runtimes (Machine.runtimes)
// ---------------------------------------------------------------------------

/** One agent CLI on one machine, as its adapter's detect() found it. */
export interface AgentRuntime {
  kind: AgentKind;
  installed: boolean;
  /** The version the CLI reports, for example 0.46.0; null when unknown. */
  version: string | null;
  /** Where the CLI was found; null when it was not. */
  path: string | null;
  checkedAt: Ts;
  /** Why detection failed (the CLI exists but would not report a version). */
  error?: string;
  /** An ACP agent: what its `initialize` reported at its last start on this machine (ADR 0049). */
  acp?: AcpRuntimeSummary;
}

/** Built-in kinds plus ACP Registry kinds installed on the machine (raised from 16, 19 Oct 2026). */
const MAX_RUNTIMES = 64;
const short = (v: unknown, max: number): string | null => (typeof v === 'string' && v ? v.slice(0, max) : null);
const MAX_AUTH_METHODS = 8;

/** What a hub keeps of an ACP runtime summary: the known fields only, bounded. */
export function sanitizeAcpSummary(v: unknown): AcpRuntimeSummary | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const r = v as Record<string, unknown>;
  const pv = Number(r.protocolVersion);
  if (!Number.isInteger(pv) || pv < 0 || pv > 1000) return undefined;
  const b = (x: unknown) => x === true;
  const sess = (r.sessions && typeof r.sessions === 'object' ? r.sessions : {}) as Record<string, unknown>;
  const mcp = (r.mcp && typeof r.mcp === 'object' ? r.mcp : {}) as Record<string, unknown>;
  const info = r.agentInfo && typeof r.agentInfo === 'object' ? (r.agentInfo as Record<string, unknown>) : null;
  const name = info ? short(info.name, 80) : null;
  const version = info ? short(info.version, 60) : null;
  const authMethods: AcpRuntimeSummary['authMethods'] = [];
  for (const m of Array.isArray(r.authMethods) ? r.authMethods.slice(0, MAX_AUTH_METHODS) : []) {
    if (!m || typeof m !== 'object') continue;
    const x = m as Record<string, unknown>;
    const id = short(x.id, 80);
    if (!id) continue;
    authMethods.push({ id, name: short(x.name, 120) ?? id, type: x.type === 'terminal' ? 'terminal' : 'agent' });
  }
  const config = sanitizeAcpConfig(r.config);
  return {
    protocolVersion: pv,
    ...(name ? { agentInfo: { name, ...(version ? { version } : {}) } } : {}),
    loadSession: b(r.loadSession),
    sessions: { list: b(sess.list), resume: b(sess.resume), close: b(sess.close) },
    embeddedContext: b(r.embeddedContext),
    mcp: { http: b(mcp.http), sse: b(mcp.sse) },
    authMethods,
    signedIn: r.signedIn === true ? true : r.signedIn === false ? false : null,
    checkedAt: typeof r.checkedAt === 'number' && Number.isFinite(r.checkedAt) ? r.checkedAt : 0,
    ...(config ? { config } : {}),
  };
}

/**
 * What a hub keeps of advertised session config: the three categories Orgus reads, each with a
 * string id, at most ACP_CONFIG_MAX_VALUES values (first ones kept: the agent's order), every
 * value and name clipped, duplicates dropped. undefined when nothing usable is left.
 */
export function sanitizeAcpConfig(v: unknown): AcpAdvertisedConfig | undefined {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined;
  const r = v as Record<string, unknown>;
  const out: AcpAdvertisedConfig = {};
  for (const cat of ACP_CONFIG_CATEGORIES) {
    const o = r[cat];
    if (!o || typeof o !== 'object' || Array.isArray(o)) continue;
    const x = o as Record<string, unknown>;
    const id = short(x.id, 100);
    if (!id) continue;
    const seen = new Set<string>();
    const values: AcpConfigValue[] = [];
    for (const e of Array.isArray(x.values) ? x.values.slice(0, ACP_CONFIG_MAX_VALUES * 2) : []) {
      if (values.length >= ACP_CONFIG_MAX_VALUES) break;
      if (!e || typeof e !== 'object') continue;
      const ev = e as Record<string, unknown>;
      const value = typeof ev.value === 'string' && ev.value && ev.value.length <= ACP_CONFIG_VALUE_MAX ? ev.value : null;
      if (!value || seen.has(value)) continue;
      seen.add(value);
      values.push({ value, name: short(ev.name, ACP_CONFIG_NAME_MAX) ?? value });
    }
    const cur = typeof x.current === 'string' && x.current.length <= ACP_CONFIG_VALUE_MAX ? x.current : null;
    out[cat] = { id, current: cur, values, ...(x.via === 'legacy' ? { via: 'legacy' as const } : {}) };
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * What a hub keeps of an agent's runtime report: well-formed entries only, bounded, with
 * strings clipped. undefined when the report carries no list (an agent that predates it).
 */
export function sanitizeRuntimes(v: unknown): AgentRuntime[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out: AgentRuntime[] = [];
  for (const x of v.slice(0, MAX_RUNTIMES)) {
    if (!x || typeof x !== 'object') continue;
    const r = x as Record<string, unknown>;
    if (!isAgentKindToken(r.kind)) continue;
    const entry: AgentRuntime = {
      kind: r.kind,
      installed: r.installed === true,
      version: short(r.version, 60),
      path: short(r.path, 400),
      checkedAt: typeof r.checkedAt === 'number' && Number.isFinite(r.checkedAt) ? r.checkedAt : 0,
    };
    const error = short(r.error, 300);
    if (error) entry.error = error;
    const acp = sanitizeAcpSummary(r.acp);
    if (acp) entry.acp = acp;
    out.push(entry);
  }
  return out;
}

/** What a hub keeps of an agent's agentKinds report: valid tokens, bounded, no duplicates. */
export function sanitizeAgentKinds(v: unknown): AgentKind[] | undefined {
  if (!Array.isArray(v)) return undefined;
  return [...new Set(v.slice(0, MAX_RUNTIMES).filter(isAgentKindToken))];
}

/**
 * Can this machine's agent start a session of this kind? Claude Code always (every agent
 * starts it); another kind only on an agent that lists it in agentKinds.
 */
export function machineStartsKind(m: { agentKinds?: AgentKind[] } | null | undefined, kind: AgentKind): boolean {
  if (kind === DEFAULT_AGENT_KIND) return true;
  return !!m?.agentKinds?.includes(kind);
}

/**
 * Can this machine's agent resume an ended session of this kind headless (a wake)? The kinds it
 * lists in resumeKinds; an agent that lists none predates the list and resumes Claude Code only.
 */
export function machineResumesKind(m: { resumeKinds?: AgentKind[] } | null | undefined, kind: AgentKind | null | undefined): boolean {
  const k = kind || DEFAULT_AGENT_KIND;
  if (!m?.resumeKinds) return k === DEFAULT_AGENT_KIND;
  return m.resumeKinds.includes(k);
}

/**
 * The Orgus permission mode a resumed session asks for, from what its session reports. Codex
 * reports its sandbox (read-only, workspace-write, danger-full-access); each maps to the Orgus
 * mode that never gives more than it had: danger-full-access to Accept edits, which is a direct
 * run where the machine keeps no Codex sandbox and workspace-write where it does. Anything else
 * that is not one of the four modes is left out (the agent's own default).
 */
export function resumePermissionMode(kind: AgentKind | null | undefined, mode: string | null | undefined): string | undefined {
  if (!mode) return undefined;
  if (mode === 'default' || mode === 'acceptEdits' || mode === 'plan' || mode === 'bypassPermissions') return mode;
  if (kind === 'codex') return mode === 'read-only' ? 'plan' : mode === 'workspace-write' || mode === 'danger-full-access' ? 'acceptEdits' : undefined;
  return undefined;
}

/** The runtime of a kind on a machine, if that machine reported one. */
export function runtimeOf(m: { runtimes?: AgentRuntime[] } | null | undefined, kind: AgentKind): AgentRuntime | null {
  return m?.runtimes?.find((r) => r.kind === kind) ?? null;
}

/** The models an ACP kind advertised on a machine (its last session's `configOptions`), in the agent's order; [] when none. */
export function advertisedModelsOn(m: { runtimes?: AgentRuntime[] } | null | undefined, kind: AgentKind): string[] {
  return advertisedValues(runtimeOf(m, kind)?.acp, 'model');
}

// ---------------------------------------------------------------------------
// Canonical state
// ---------------------------------------------------------------------------

/**
 * The canonical state of any session: the adapter's own mapping when it reported one,
 * else derived from the four-state status every agent reports.
 */
export function sessionStateOf(s: Pick<Session, 'status' | 'turns' | 'toolCalls' | 'lastText' | 'state'>): CanonicalSessionState {
  // An older agent may call a session that ended on an API error done: its last words say otherwise.
  if (s.state) return s.state === 'done' && sessionFailed(s) ? 'failed' : s.state;
  return stateFromStatus(s);
}

/**
 * status to canonical: ended is done, or failed when its last message is the error Claude Code
 * printed instead of an answer (a model the account cannot use, an API error); a live session
 * that has not finished a turn is starting.
 */
export function stateFromStatus(s: Pick<Session, 'status' | 'turns' | 'toolCalls' | 'lastText'>): CanonicalSessionState {
  const fresh = s.turns === 0 && s.toolCalls === 0 && !s.lastText;
  switch (s.status) {
    case 'ended':
      return sessionFailed(s) ? 'failed' : 'done';
    case 'waiting':
      return 'waiting_for_human';
    case 'working':
      return fresh ? 'starting' : 'working';
    case 'idle':
      return fresh ? 'starting' : 'idle';
  }
}

/** canonical to status, for the parts of the UI that render the four-state status. */
export function statusFromState(state: CanonicalSessionState): SessionStatus {
  switch (state) {
    case 'starting':
    case 'working':
      return 'working';
    case 'waiting_for_human':
      return 'waiting';
    case 'idle':
      return 'idle';
    default:
      return 'ended';
  }
}

// ---------------------------------------------------------------------------
// Codex (Session.ext when agentKind is 'codex')
// ---------------------------------------------------------------------------

/**
 * Where a Codex session is in its own lifecycle, from its rollout:
 *   new         session_meta written, no turn yet
 *   turn        a task_started without its task_complete or turn_aborted
 *   complete    the last turn finished (task_complete)
 *   aborted     the last turn was interrupted or replaced (turn_aborted)
 *   failed      the last turn ended with an error (task_complete with error, turn.failed)
 */
export type CodexNativeState = 'new' | 'turn' | 'complete' | 'aborted' | 'failed';

/** How Orgus knows a Codex session is still running. */
export type CodexLiveness = 'own' | 'inferred' | 'none';

export interface CodexSessionExt {
  native: CodexNativeState;
  liveness: CodexLiveness;
  cliVersion: string | null;
  /** codex_cli_rs, codex_exec, codex_vscode, ... */
  originator: string | null;
  /** cli, exec, vscode, mcp, subagent, ... */
  source: string | null;
  approvalPolicy: string | null;
  sandbox: string | null;
  effort: string | null;
  reasoningTokens: number;
  /** The thread this one was forked from, or its parent for a sub-agent. */
  parentId: string | null;
  /** With liveness 'inferred': how many Codex processes the guess came from. */
  processCount?: number;
}

/** Inputs for the Codex mapping (pure, so it is tested directly). */
export interface CodexStateInput {
  native: CodexNativeState;
  /** A process is running it (one Orgus started, or inferred from the process table). */
  alive: boolean;
  /** The last agent message of a finished turn ends in a question. */
  asks: boolean;
}

/**
 * Codex onto the canonical machine:
 *   alive:  new -> starting, turn -> working, complete -> idle (waiting_for_human when it
 *           asked a question), aborted -> idle, failed -> idle
 *   ended:  new -> stopped, turn -> stopped (it died mid-turn), complete -> done,
 *           aborted -> stopped, failed -> failed
 */
export function codexState(i: CodexStateInput): CanonicalSessionState {
  if (i.alive) {
    switch (i.native) {
      case 'new':
        return 'starting';
      case 'turn':
        return 'working';
      case 'complete':
        return i.asks ? 'waiting_for_human' : 'idle';
      default:
        return 'idle';
    }
  }
  switch (i.native) {
    case 'complete':
      return 'done';
    case 'failed':
      return 'failed';
    default:
      return 'stopped';
  }
}

/** Sandbox mode for an Orgus permission mode (or a Codex sandbox name given directly). */
export const CODEX_SANDBOX_FOR_PERMISSION: Readonly<Record<string, string>> = Object.freeze({
  plan: 'read-only',
  acceptEdits: 'workspace-write',
  bypassPermissions: 'danger-full-access',
  'read-only': 'read-only',
  'workspace-write': 'workspace-write',
  'danger-full-access': 'danger-full-access',
});

/** The sandbox for a permission mode, or null: own keys only, so "toString" or "__proto__" never match. */
export function codexSandboxFor(mode: string): string | null {
  if (!Object.hasOwn(CODEX_SANDBOX_FOR_PERMISSION, mode)) return null;
  const v = CODEX_SANDBOX_FOR_PERMISSION[mode];
  return typeof v === 'string' ? v : null;
}

/** The one command that resumes a session of this kind in a terminal. */
export function resumeArgv(kind: AgentKind, id: string): string[] | null {
  if (kind === 'codex') return ['codex', 'resume', id];
  if (kind === 'claude-code') return ['claude', '--resume', id];
  return null;
}
